import type { ComponentActionType } from "@v1/types/component-action-type";
import type {
  CoreState,
  ElementsState,
  HistoryStateType,
} from "@v1/types/core-state";
import type { GetAttributeFunction } from "@v1/types/get-attribute-function";
import type { ComponentMapping } from "replo-runtime/shared/Component";
import type { ReploMixedStyleValue } from "replo-runtime/store/utils/mixed-values";
import type { Component } from "schemas/component";
import type { ReploElement } from "schemas/generated/element";
import type { BoxShadow } from "schemas/runtimeStyles";
import type { TextShadow } from "schemas/textShadow";

import { applyComponentAction } from "@v1/reducers/utils/component-actions";
import { parseBoxShadows } from "@v1/utils/boxShadow";
import { getEdgeAttribute } from "@v1/utils/component";
import { objectId } from "@v1/utils/objectId";
import { parseTextShadows } from "@v1/utils/textShadow";
import { enablePatches, produceWithPatches } from "immer";
import isEqual from "lodash-es/isEqual";
import omit from "lodash-es/omit";
import { forEachComponentAndDescendants } from "replo-runtime/shared/utils/component";
import { getFromRecordOrNull } from "replo-runtime/shared/utils/optional";
import { REPLO_MIXED_STYLE_VALUE } from "replo-runtime/store/utils/mixed-values";
import { filterNulls } from "replo-utils/lib/array";

enablePatches();

/**
 * Takes an action and applies it to a component.
 *
 * Does not handle creating a component
 */
export const getNextElements = ({
  action,
  state,
}: {
  action: { payload: ComponentActionType };
  state: CoreState;
}) => {
  const op = action.payload;
  const elements = state.elements.mapping;
  let updatedComponentIds: string[] = [];

  const [nextState, patches, inversePatches] = produceWithPatches(
    elements,
    (elements) => {
      updatedComponentIds = applyComponentAction({
        action: { ...op, analyticsExtras: action.payload.analyticsExtras },
        elements,
        state,
      });
    },
  );
  return {
    elements: nextState,
    patches,
    inversePatches,
    updatedComponentIds,
  };
};

export const getHistoryAfterPatch = ({
  action,
  history,
  patches,
  inversePatches,
  elementId,
  componentIds,
}: {
  action: { type: any };
  history: HistoryStateType;
  patches: any;
  inversePatches: any;
  elementId: string;
  componentIds: string[];
}) => {
  const next = history.index + 1;
  const operation = {
    type: action.type,
    redo: patches,
    undo: inversePatches,
    elementId,
    componentIds,
  };

  history.operations[next] = operation;

  /* Delete one version in advance so we cannot redo */
  delete history.operations[next + 1];
  /* Delete anything that's more than the number of supported versions */
  delete history.operations[history.index - history.maxSize];

  return {
    ...history,
    operations: history.operations,
    index: next,
  };
};

type PaintTypeResult =
  | { paintType: "none" }
  | { paintType: "all"; forceRemount: boolean }
  | { paintType: "components" };

export type EditorRepaintDependencies = {
  elementMapping: ElementsState["mapping"];
  draftElementId: string | null;
};

export const editorNeedsRepaint = (
  prevDependencies: EditorRepaintDependencies | null,
  currentDependencies: EditorRepaintDependencies,
): PaintTypeResult => {
  if (
    !prevDependencies ||
    !isEqual(
      prevDependencies.draftElementId,
      currentDependencies.draftElementId,
    )
  ) {
    return {
      paintType: "all",
      forceRemount: true,
    };
  }

  const previousElement = getFromRecordOrNull(
    prevDependencies.elementMapping,
    prevDependencies.draftElementId,
  );
  const currentElement = getFromRecordOrNull(
    currentDependencies.elementMapping,
    currentDependencies.draftElementId,
  );

  // NOTE (Martin, 2024-09-28): We first elements list separatedly from each
  // element component tree data, so we need to take that into account.
  const isElementComponentLoaded =
    previousElement?.id === currentElement?.id &&
    !previousElement?.component &&
    currentElement?.component;

  // NOTE (Martin, 2024-09-28): If the user has made an update to the element
  // but we've rejected the update on the backend and returned the most up to
  // date version, we want to repaint because the user needs to see the latest
  // version. In this case, the previous element component tree will be a
  // different object in-memory than the current one (since in core-reducer,
  // we reset the element mapping to have the new element from the server).
  const isDifferentElementComponentTree =
    previousElement?.component &&
    currentElement?.component &&
    objectId(previousElement.component) !== objectId(currentElement.component);

  if (isElementComponentLoaded || isDifferentElementComponentTree) {
    return { paintType: "all", forceRemount: false };
  }

  return {
    paintType: "none",
  };
};

export function getComponentMappingFromElement(
  draftElement: ReploElement | null,
) {
  if (!draftElement) {
    return {};
  }
  const mapping: ComponentMapping = {};
  forEachComponentAndDescendants(
    draftElement.component,
    (component, parentComponent) => {
      mapping[component.id] = {
        component,
        parentComponent,
      };
    },
  );
  return mapping;
}

export function getFieldMapping<
  KeyType extends string,
  RecordType extends Record<KeyType, any>,
>(
  iterable: RecordType[],
  field: KeyType,
): Record<RecordType[KeyType], RecordType> {
  const mapping: Record<string, RecordType> = {};
  iterable.forEach((item) => {
    mapping[String(item[field])] = item;
  });
  return mapping;
}

export const orderOfCategories: Record<string, number> = {
  Basic: 1,
  Layout: 2,
  Product: 3,
  Video: 4,
  Form: 5,
  Map: 6,
  Icon: 7,
  Component: 8,
  "Custom Code": 9,
  CMS: 10,
};

export type PositionAttribute = "top" | "bottom" | "left" | "right";

export function getDefaultPositionAttribute(
  positions: PositionAttribute[],
  draftComponent: Component,
  getAttribute: GetAttributeFunction,
): PositionAttribute | "center" {
  for (const position of positions) {
    const { value } = getAttribute(draftComponent, `style.${position}`);

    if (value === "50%") {
      return "center";
    }
    // Note (Noah, 2021-08-11): Unfortunately it seems as if some components have
    // acquired values of NaNpx for their positioning values. We should probably
    // write a migration to remove these but for now we just handle them the same
    // as null
    if (value !== null && value !== "auto" && value !== "NaNpx") {
      return position;
    }
  }
  return positions[0]!;
}

export const hardcodedActions = [
  "redirect",
  "addProductVariantToCart",
  "clearCart",
  "redirectToProductPage",
  "applyDiscountCode",
  "clearDiscountCode",
  "executeJavascript",
  "scrollToUrlHashmark",
] as const;

/**
 * Note (Chance 2023-07-11) This function should only return an updated element
 * state reference if the mapping or versionMapping have changed, based on the
 * new element and version. This will prevent over-calling effects that depend
 * on the elements state.
 */
export function mergeElementsStateWithNewElement(
  elements: ElementsState,
  element: ReploElement & { version: number },
): ElementsState {
  const mapping = Object.is(elements.mapping[element.id], element)
    ? elements.mapping
    : { ...elements.mapping, [element.id]: element };
  const versionMapping =
    elements.versionMapping[element.id] === element.version
      ? elements.versionMapping
      : { ...elements.versionMapping, [element.id]: element.version };

  if (
    Object.is(elements.mapping, mapping) &&
    Object.is(elements.versionMapping, versionMapping)
  ) {
    return elements;
  }

  return { ...elements, mapping, versionMapping };
}

/**
 * Note (Chance 2023-07-11) This function should only return an updated elements
 * state reference if the draft element exists and needs to be updated with a
 * new component reference. This will prevent over-calling effects that depend
 * on the elements state.
 *
 * Note (Evan, 2024-07-18): We parameterize the properties of the elements state actually
 * used in the calculation to avoid triggering recalculation when irrelevant properties
 * (particularly in the streamingUpdate) change. See selectElementWithRevisionState in
 * core-reducer.ts for more details.
 */
export function enhanceElementMapping({
  draftElementId,
  elementRevisions,
  selectedRevisionId,
  elementMapping,
  streamingDraftElementComponent,
  selectedArchivedElementId,
  archivedElements,
}: {
  draftElementId: string | null;
  elementRevisions: ElementsState["elementRevisions"];
  selectedRevisionId: string | null;
  elementMapping: ElementsState["mapping"];
  streamingDraftElementComponent: Component | undefined;
  selectedArchivedElementId: string | null;
  archivedElements?: ReploElement[];
}): ElementsState["mapping"] {
  if (!draftElementId) {
    return elementMapping;
  }
  const draftElement = elementMapping[draftElementId];
  if (!draftElement) {
    return elementMapping;
  }

  const archivedElement = archivedElements?.find(
    (archivedElement) => archivedElement.id === selectedArchivedElementId,
  );

  const updatedDraftElementComponentFromRevision = elementRevisions[
    draftElementId
  ]?.find((revision) => revision.id === selectedRevisionId)?.component;

  const updatedDraftElementComponentFromArchived = archivedElement?.component;

  const updatedDraftElementComponentFromStreaming =
    draftElement.component.id === streamingDraftElementComponent?.id &&
    streamingDraftElementComponent;

  const updatedDraftElementComponent =
    updatedDraftElementComponentFromRevision ??
    updatedDraftElementComponentFromArchived ??
    updatedDraftElementComponentFromStreaming;
  if (
    !updatedDraftElementComponent ||
    Object.is(updatedDraftElementComponent, draftElement.component)
  ) {
    return elementMapping;
  }
  if (selectedArchivedElementId && archivedElement) {
    return {
      ...elementMapping,
      [selectedArchivedElementId]: archivedElement,
    };
  }
  return {
    ...elementMapping,
    [draftElementId]: {
      ...draftElement,
      component: updatedDraftElementComponent as Component,
    },
  };
}

export function findComponentsShadows<
  ShadowKindType extends "boxShadow" | "textShadow",
>(
  kind: ShadowKindType,
  components: Component[],
  getAttribute: GetAttributeFunction,
): ShadowKindType extends "boxShadow"
  ? (BoxShadow | ReploMixedStyleValue)[]
  : (TextShadow | ReploMixedStyleValue)[] {
  const componentsShadows = components.map((component) => {
    const shadow = getEdgeAttribute(component, kind, getAttribute);

    if (!shadow) {
      return [];
    }

    return kind === "boxShadow"
      ? parseBoxShadows(shadow.split(","))
      : parseTextShadows(shadow.split(","));
  });

  // Find the length of the longest array
  const maxLength = componentsShadows.reduce((max, componentShadows) => {
    return Math.max(max, componentShadows.length);
  }, 0);

  return filterNulls(
    Array.from({ length: maxLength }, (_, index) => {
      const currentShadows = componentsShadows.map((shadow) => shadow[index]);
      const firstValue = currentShadows[0];
      const hasMixedValues = currentShadows.some(
        (shadow) => !isEqual(omit(shadow, "id"), omit(firstValue, "id")),
      );
      return hasMixedValues ? REPLO_MIXED_STYLE_VALUE : firstValue;
    }),
  ) as any; // Type assertion needed due to TypeScript limitations
}
